// The play profile: what the opponent is trying to do.
//
// A *difficulty* says how well it plays — how often it blunders, how far it
// looks. A profile says what it is trying to *do*: two `Skill` knobs
// (`tuning`) and a handful of evaluation opinions (`weights`).
//
// **One profile, the Robotic Monarchies'** (§31 R5, Q7). A 2026-10-26 round
// robin on the current rules (42 games) had it lead every factory measure —
// pieces minted, value minted, forges, captures, biggest army — at 57%,
// inside the noise floor of the combat style that won. Every other style,
// champion and tuner goal was scrapped. The table stays a table so the
// tuner's tooling still reads it.
//
// **A profile is a style; a champion is a search result.** `champions.json`
// holds what the search last found under the `robotic-monarchies` goal
// (`tools/profiles.json`), copied in by `npm run promote` and committed. A
// champion overrides `weights` as a ratio (`styleWeights`) and nothing else.
// Today it is a stub (§32 D1), so the hand-written weights play.

import type { Side } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { areEnemies } from "src/engine/rules/position";
import type { Tuning } from "./difficulty";
import { type Weights, WEIGHTS,
  PLANNER_VERSION,
  migrateWeights,
  withWeights
} from "./tuning";
import CHAMPIONS from "./champions.json";

export interface PlayProfile {
  id: string;
  name: string;
  /** One line, for the control's `title`. */
  detail: string;
  /**
   * The tuner goal this style is searched under, when it is not the id.
   *
   * An id is what a player sees, a goal name is what a fitness function is
   * called; absent, the id is the goal. A miss is silent — an unmatched key
   * keeps the hand-written weights — so `profiles.test.ts` asserts every
   * champion is claimed.
   */
  champion?: string;
  tuning?: Tuning;
  weights?: Partial<Weights>;
}

/** The shipped set: the Robotic Monarchies' profile, and nothing else. */
export const PLAY_PROFILES: readonly PlayProfile[] = [
  {
    id: "robotic-monarchies",
    name: "Robotic Monarchies",
    detail:
      "Covers the board and moves what it digs. Lays track, keeps the lanes loaded and the floor clean — and spends the metal rather than sitting on it.",
    tuning: { aggression: 10, greed: 34 },
    weights: {
      // Its decisions are mostly interactions — material matters less once
      // the factory runs, ore more while it is built — which is a shape, and
      // shape is what the head is for.
      head: 300,
      // Material has somewhere to be: believe the factory's valuation of a
      // haul, and pay per square closed on somewhere to put a load down.
      haul: 34,
      flowUrgency: 1.6,
      // A load is worth carrying and therefore worth not losing.
      cargoAtRisk: 6,
      mining: 12,
      looseScrap: 5,
      // Metal in a hopper is a decision nobody has made.
      banked: 0.5
    }
  }
] as const;

export const DEFAULT_PROFILE = PLAY_PROFILES[0].id;

/**
 * The profile a run's *enemies* play. Since R5 it is the only one; kept as
 * its own name because `profileForSide` answers "enemy of the run" with it.
 */
export const RUN_OPPONENT_PROFILE = DEFAULT_PROFILE;

/**
 * Which profile this side plays, before the caller's own choice: a run's
 * enemies name `RUN_OPPONENT_PROFILE`, everybody else falls through to the
 * default. With one profile both answers play the same army.
 */
export function profileForSide(state: GameState, side: Side): string | undefined {
  const run = state.run;
  if (run && areEnemies(state.position, side, run.side)) return RUN_OPPONENT_PROFILE;
  return undefined;
}

/**
 * What the tuner found, if anybody has promoted it (`npm run promote`).
 *
 * Committed, because `.tuning/` is not: a champion that only exists on the
 * machine that produced it cannot reach a build. Promotion is a deliberate
 * step and refuses to cross a `METRICS_VERSION` bump without being told —
 * shipping weights tuned against an economy that has since changed reads as
 * the opponent having got worse for no reason.
 */
interface Champion {
  /**
   * Null on a **stub** — a goal somebody has authored and nothing has searched
   * yet. A stub exists so the wiring is complete and visible before the first
   * pass lands: `profiles.test.ts` checks every tuner goal has an entry here,
   * which is what catches a goal reaching the box untrained.
   *
   * It must carry no score and **no weights**. A champion's content is a ratio
   * against the defaults (`styleWeights`), so a stub seeded with its own
   * style's numbers applies them to themselves twice — measured, a declared
   * `regentValue` of 4,000 came back as 13,333. Empty is the identity.
   */
  score?: number | null;
  stub?: boolean;
  generation?: number | null;
  metricsVersion?: number | null;
  /**
   * The planner this vector was searched under, which is what `carryForward`
   * migrates from. Absent on a stub and on anything promoted before the field
   * existed; nought is the honest reading of both, since every migration then
   * applies and each one refuses a vector of the wrong length anyway.
   */
  plannerVersion?: number | null;
  /**
   * **Optional, because a stub has none.** A champion's content is a *ratio*
   * against the defaults (`styleWeights`), so a stub seeded with its own
   * style's numbers would apply them to themselves twice — measured, a declared
   * `regentValue` of 4,000 came back as 13,333. Absent is the identity, and
   * `styleWeights` already reads a missing vector as "the style stands".
   */
  weights?: Partial<Weights>;
}
const PROMOTED = CHAMPIONS as Record<string, Champion | undefined>;

/**
 * A stored vector brought up to this planner, or left exactly as it was.
 *
 * **The game read path never migrated, and the cost of that was the whole
 * committed field.** `MIGRATIONS` exists precisely so a champion survives a
 * planner bump — `resizeParams` is behaviour-preserving in the grow direction,
 * so a widened network computes what it computed before, to the last bit — and
 * `migrateWeights` was called from `tools/lib/island.mjs` and from nowhere
 * else. So a stored head of the wrong length reached `withWeights`, where
 * `run` refuses it, `isFlatHead` calls it dead and `reviveHead` replaces it
 * with a salted hash. Measured across the committed file at planner 21: **ten
 * of eleven champions carried a 59-parameter head and one carried 32**, so
 * every profile in the offline field was playing a hash — at its own
 * authority, up to 400 centipawns of it.
 *
 * **Forward only, and the stored version is left where it is.** A migration
 * carries the weights; it does not re-measure the score, and `plannerVersion`
 * is what says which planner that score came from. Bumping it here would claim
 * a measurement nobody took.
 */
function carryForward(champion?: Champion): Champion | undefined {
  if (!champion?.weights) return champion;
  const from = champion.plannerVersion ?? 0;
  if (from >= PLANNER_VERSION) return champion;
  const { weights } = migrateWeights(
    champion.weights as Record<string, unknown>,
    from
  );
  return { ...champion, weights };
}

/**
 * The committed field, carried forward once at load rather than per read.
 *
 * A migration is a pure function of a stored vector, so doing it here is a memo
 * and not module state in the sense `src/engine/ai/` forbids — there is nothing to
 * invalidate and no clock in it. Once, because `championOf` is asked for every
 * profile the shelf draws as well as for every game that opens.
 */
const CARRIED: Record<string, Champion | undefined> = Object.fromEntries(
  Object.entries(PROMOTED).map(([key, champion]) => [key, carryForward(champion)])
);

/**
 * A champion's finding, expressed at the style's own level.
 *
 * **The merge used to be `{ ...declared, ...champion }`, and a champion holds
 * every one of the fifty-nine weights — so the spread was a total override and
 * a profile's declared numbers were dead the moment anybody promoted
 * anything.** Measured: one profile declared `mining: 2` and played 6.82,
 * another declared 16 and played 8.95 — every style dragged back to about the
 * default, in both directions. A champion is seeded from the defaults and
 * searched from there (`tools/tune.mjs`), so it lands near them, and a spread
 * then throws the style away.
 *
 * So the merge keeps both halves of what it knows. A champion's real finding
 * is not `mining should be 6.82` — it is **`mining should be 0.85 of the
 * default`**, which is a claim about the shape of the evaluation and is worth
 * carrying. The style's claim is about the *level*. Applying the champion's
 * ratio to the declared value keeps both: a declared 2 becomes 1.7 rather
 * than 6.82.
 *
 * Where the ratio means nothing — a default of zero, a sign flip, anything not
 * a finite number — the **style wins**. A declared weight is somebody's stated
 * intent and the fallback should not quietly be the thing this exists to stop.
 */
export function styleWeights(
  declared: Partial<Weights> | undefined,
  champion: Partial<Weights> | undefined
): Partial<Weights> | undefined {
  if (!champion) return declared;
  if (!declared) return champion;

  const out: Record<string, unknown> = { ...champion };
  for (const [key, level] of Object.entries(declared)) {
    const base = numberAt(WEIGHTS, key);
    const found = numberAt(champion, key);
    const usable =
      typeof level === "number" &&
      base !== undefined &&
      found !== undefined &&
      base !== 0 &&
      Math.sign(base) === Math.sign(found);
    if (!usable || typeof level !== "number" || base === undefined || found === undefined) {
      out[key] = level;
      continue;
    }
    const scaled = level * (found / base);
    // **A style's *direction* survives promotion, not only its ratio.**
    //
    // The ratio carries the champion's finding about shape and the declared
    // value carries the style's claim about level — but a strong enough ratio
    // takes the product past the default and *inverts* the claim. Measured: a
    // style declaring `mining: 10` against a default of 8 met a 0.57x champion
    // and played 5.74 — the field collapsing to one army by a slower road than
    // the total override this function already exists to stop.
    //
    // **The style wins, exactly as it does for a zero default or a sign flip.**
    // A ratio that contradicts what the style is *for* is meaningless for that
    // weight, which is the same category as the cases above — and clamping to
    // the default instead lands *on* it, erasing the claim just as surely.
    // Where the two do not disagree, which is the ordinary case, this changes
    // nothing at all.
    //
    // **Which side of the default, and "the same side" includes *on* it.**
    // Written as a pair of comparisons this had a hole at `level === base`: a
    // style declaring exactly the baseline is still making a claim — mine at
    // the baseline rate — and neither branch tested for it, so whichever
    // direction the ratio pulled, one of them went unchecked (a declared 8 at
    // a 0.81x champion played 6.45). Comparing signs is the same rule for all
    // three cases and is the predicate `profiles.test.ts` states.
    const inverts = Math.sign(scaled - base) !== Math.sign(level - base);
    out[key] = inverts ? level : scaled;
  }
  return out as Partial<Weights>;
}

/**
 * One weight, when it is a plain number.
 *
 * `Weights` holds arrays as well — `piece` is one per rank — and a ratio is
 * meaningless for those. Via `unknown` because `Weights` has no index
 * signature, which is the type system correctly pointing out that this is a
 * lookup by a name it cannot check.
 */
function numberAt(source: object, key: string): number | undefined {
  const value = (source as unknown as Record<string, unknown>)[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * The profile with this id, or the default. Never throws on a stale id.
 *
 * **A promoted champion is a finding, not a replacement** — see `styleWeights`
 * for what that costs and why it is not a spread. And it only ever touches the
 * weights: `tuning` — how hard it comes for you, how much it works the factory
 * — stays what somebody wrote down, because the search does not optimise those
 * two and a champion has no opinion about them. A profile with no champion
 * keeps its numbers, which is what makes the table readable on a clean
 * checkout where nobody has run the tuner at all.
 */
export function profileById(id: string | undefined): PlayProfile {
  const base = PLAY_PROFILES.find((entry) => entry.id === id) ?? PLAY_PROFILES[0];
  const champion = championOf(base);
  if (!champion) return base;
  return { ...base, weights: styleWeights(base.weights, champion.weights) };
}

/**
 * Champions the server has, which the tuner published after the build shipped.
 *
 * **This is what stops a tuning run needing a deploy.** `champions.json` is
 * committed and therefore always as old as the last release; the box searches
 * continuously and had no way to reach a player. Fetched once at boot
 * (`loadLiveChampions`), applied here, and falling back to the committed file
 * for anything it does not carry.
 *
 * Module state, which `src/engine/ai/` otherwise forbids on determinism grounds — the
 * exception holds because of *when* it is written. It is set once before a
 * game starts and never during one, and the weights it produces are resolved
 * in the main thread and sent **in the plan request**, so the worker plays
 * exactly what the main thread resolved rather than resolving again from state
 * it might not share. A game is still a pure function of its log and the
 * vector it opened with.
 */
let live: Record<string, Partial<Weights>> | null = null;

/**
 * Take the server's champions, carrying an older one forward, or refuse them.
 *
 * **An older payload is migrated; a newer one is refused; and the asymmetry is
 * what the two directions actually mean.** Behind is a planner this build knows
 * every step of — `MIGRATIONS` holds them and `resizeParams` grows a head
 * without changing what it computes — so a vector from behind is carried
 * forward exactly as `tools/lib/island.mjs` carries one. Ahead is a term this
 * build has never heard of, in a slot it cannot place, and there is nothing to
 * do with it but decline.
 *
 * **The refusal used to be both directions, and what it fell back to was not
 * "what shipped".** It was a *hash*: the committed file is older still, so a
 * planner bump reaching `deploy` before the tuner republished took the entire
 * field through `reviveHead` — eleven armies playing salted seeds at their own
 * authority. Measured at planner 21 with the live feed accepted, the same
 * eleven were the tuner's real vectors; the gate is the only thing between
 * those two states, and it flips on a version number that moves every time
 * anybody adds a weight.
 *
 * What is given up is that a migrated vector's *score* was measured under the
 * old planner, which is exactly the trade a bump already makes everywhere else
 * — the tuner re-measures, and a game cannot. A vector chosen against a
 * slightly different `evaluate` still beats a hash chosen against nothing.
 *
 * Returns how many were taken, so a caller can say so rather than guess.
 */
export function applyLiveChampions(
  payload: {
    plannerVersion?: number;
    champions?: Record<string, unknown>;
    /**
     * Which planner each entry was measured under, where the server knows.
     *
     * **A mixed field is the ordinary case, not a corner.** Islands search
     * independently, so a bump reaches each profile whenever its pass next
     * turns over; measured on the deployment, ten were at 21 and one at 20.
     * Falls back to the payload's own version, which is what a tuner running
     * older code publishes and is right for a field that has caught up.
     */
    plannerVersions?: Record<string, number>;
  } | null
): number {
  if (!payload?.champions) return 0;
  // **An absent version is refused, and it is not the same case as an old
  // one.** Migrating needs a planner to migrate *from*; nought would run every
  // migration in the list, each of which refuses a vector whose length does not
  // match the shape it names — so a modern vector would sail through untouched
  // and be played as though somebody had vouched for it. Unknown provenance is
  // what the guard was written for.
  const published = payload.plannerVersion;
  if (typeof published !== "number" || published > PLANNER_VERSION) return 0;

  const taken: Record<string, Partial<Weights>> = {};
  for (const [name, weights] of Object.entries(payload.champions)) {
    if (!weights || typeof weights !== "object") continue;
    const from = payload.plannerVersions?.[name] ?? published;
    // A single entry from ahead of this build is skipped rather than taken at
    // face value, exactly as a whole payload from ahead is refused.
    if (from > PLANNER_VERSION) continue;
    // **Validated before it is believed.** These arrive over the wire from
    // another machine; a non-finite entry does not throw here, it throws
    // inside the planner several moves later as a weight that is not a
    // number. `withWeights` is the one place that already knows the rule.
    try {
      const carried =
        from < PLANNER_VERSION
          ? migrateWeights(weights as Record<string, unknown>, from).weights
          : (weights as Partial<Weights>);
      withWeights(carried);
      taken[name] = carried;
    } catch {
      // One bad vector must not cost the rest of the field.
    }
  }
  live = Object.keys(taken).length > 0 ? taken : null;
  return Object.keys(taken).length;
}

/** Forget the server's champions and play what shipped. For tests. */
export const clearLiveChampions = (): void => {
  live = null;
};

/**
 * The promoted champion for a style, under whichever goal it was searched as.
 *
 * The server's copy first, the committed one behind it — so a profile the
 * tuner has not reached keeps the champion that shipped with the build rather
 * than losing one.
 */
const championOf = (profile: PlayProfile): Champion | undefined => {
  const key = profile.champion ?? profile.id;
  const fresh = live?.[key];
  // Already carried forward by `applyLiveChampions`, which knows the planner
  // the whole payload was published under — a live entry has no version of its
  // own to read here.
  if (fresh) return { weights: fresh };
  return CARRIED[key];
};

/**
 * What a profile is currently playing with, for the UI to say so.
 *
 * Through the profile rather than straight into the table, or an entry whose
 * goal is not its id reads as untuned while playing a champion.
 */
export const promotedScore = (id: string): number | undefined =>
  // `?? undefined`, because a **stub** carries an explicit null: a goal that
  // has been authored and never searched has no score, and the UI's question
  // is "is this tuned", to which null and absent are the same answer.
  championOf(profileById(id))?.score ?? undefined;
